/*
  Admin › Integrations — wave 3:
    * ServicesCard — every outside service of the shared foundation (backend/feature_flags.py) with its real
      status (On / Switched off / Setup needed), what is missing on the server (setting names only), where it
      is used, the step-by-step guide's section, "Copy setup note for IT" and — for services an admin may
      switch — the on / off switch (PUT /api/integrations/{key}, recorded in History).
    * OneDriveUnlinked (31 #5) — the newest active projects without a OneDrive folder, each with "Link folder"
      (the project's Files tab).
    * SyncActivityCard (31 #7) — every invoice sync and failure, newest first; empty state with an example; a
      failure fixed by a later sync is "Resolved" (no Retry); Retry sends the invoice again right here; the daily
      summary for Accounts (scheduled_jobs/quickbooks_daily_summary — off until Outgoing email is set up).
*/
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Activity, ArrowRight, Bot, ChevronDown, Cloud, Copy, ExternalLink, Fingerprint, Inbox, KeyRound, Link2, Loader2, Mail, MailCheck,
  Plug, ReceiptText, RotateCw, Sparkles, TriangleAlert,
} from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { StatusPill } from '@/components/ui/status-pill';
import { SystemChip } from '@/components/ui/system-chip';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { notify } from '@/components/ui/notify';
import { useIntegrationStatus } from '@/hooks/useIntegrationStatus';
import { AdminCard, UpperLabel } from '@/components/admin/AdminUi';
import { copyText } from '@/components/admin/PasswordField';
import { SERVICE_ORDER, SERVICE_SETUP, serviceSetupNote, serviceStatus } from '@/components/admin/adminModel';

const SERVICE_ICONS = {
  quickbooks: ReceiptText,
  onedrive: Cloud,
  email_outbound: Mail,
  email_inbound: Inbox,
  microsoft_sso: KeyRound,
  passkeys: Fingerprint,
  ai: Bot,
  ai_extras: Sparkles,
};

/* ------------------------------------------------------------------------------------------------ */
function ServiceRow({ feature, statusInfo, open, onToggle, onSwitch, switching, appName }) {
  const Icon = SERVICE_ICONS[feature.key] || Activity;
  const setup = SERVICE_SETUP[feature.key] || {};
  const missingCount = (feature.missing?.length || 0) + (feature.missing_modules?.length || 0);
  const canSwitch = Boolean(feature.switchable) && Boolean(feature.configured) && !feature.placeholder;
  const panelId = `service-setup-${feature.key}`;
  const copyNote = async () => {
    try {
      await copyText(serviceSetupNote(feature, { appName, origin: window.location.origin }));
      notify.success('Setup note copied', { description: `Paste it in a message to IT — it lists what ${feature.label} needs.` });
    } catch (err) {
      notify.warning('Couldn’t copy', { description: 'The browser blocked the clipboard — copy the setting names from “Setup for IT”.' });
    }
  };
  return (
    <li className="py-3" data-testid={`service-${feature.key}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <span className={cn('ui-icon-tile h-9 w-9 shrink-0', feature.status === 'on' ? 'tone-green' : 'tone-gray')} aria-hidden="true">
          <Icon className="h-[17px] w-[17px]" />
        </span>
        <div className="min-w-[200px] flex-1">
          <p className="flex flex-wrap items-center gap-2 text-[13.5px] font-semibold text-ink">
            {feature.label}
            <StatusPill tone={statusInfo.tone} label={statusInfo.label} size="sm" data-testid={`service-status-${feature.key}`} />
          </p>
          <p className="text-[12.5px] text-ink-3">
            {feature.description}
            {setup.usedIn ? <span className="text-ink-3"> · Used in: {setup.usedIn}</span> : null}
          </p>
        </div>
        <div className="flex max-w-full flex-wrap items-center gap-x-3 gap-y-2 sm:shrink-0">
          {missingCount && feature.status !== 'on' ? (
            <span className="text-[12px] font-semibold text-status-amber-ink" data-testid={`service-missing-${feature.key}`}>
              {missingCount} {missingCount === 1 ? 'thing' : 'things'} missing on the server
            </span>
          ) : null}
          {feature.switchable ? (
            <label className={cn('inline-flex items-center gap-2 text-[12.5px] text-ink-2', !canSwitch && 'cursor-not-allowed opacity-70')}>
              <Switch
                checked={feature.status === 'on'}
                onCheckedChange={(next) => onSwitch(feature, next)}
                disabled={!canSwitch || switching}
                aria-label={`${feature.label}: ${feature.status === 'on' ? 'on' : 'off'}`}
                data-testid={`service-switch-${feature.key}`}
              />
              {canSwitch ? (feature.status === 'on' ? 'On' : 'Off') : 'Off until set up'}
            </label>
          ) : null}
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={open}
            aria-controls={panelId}
            className="inline-flex items-center gap-1 rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            data-testid={`service-setup-toggle-${feature.key}`}
          >
            Setup for IT
            <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')} aria-hidden="true" />
          </button>
        </div>
      </div>
      {open ? (
        <div id={panelId} className="ml-12 mt-2.5 space-y-2 rounded-xl border border-line bg-surface-2 p-3.5 text-[12.5px] text-ink-2" data-testid={`service-setup-${feature.key}`}>
          {feature.missing?.length ? (
            <p><b className="font-semibold text-ink">Server settings to add:</b> <span className="font-mono text-[11.5px]">{feature.missing.join(' · ')}</span></p>
          ) : feature.status !== 'on' && !feature.placeholder ? (
            <p>Every server setting is in place.</p>
          ) : null}
          {feature.missing_modules?.length ? (
            <p><b className="font-semibold text-ink">Server libraries to install:</b> <span className="font-mono text-[11.5px]">{feature.missing_modules.join(' · ')}</span></p>
          ) : null}
          {(feature.problems || []).map((problem) => (
            <p key={problem} className="flex items-start gap-1.5 text-status-red-ink"><TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />{problem}</p>
          ))}
          {(feature.warnings || []).map((warning) => (
            <p key={warning} className="flex items-start gap-1.5 text-status-amber-ink"><TriangleAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />{warning}</p>
          ))}
          {feature.optional_env?.length ? (
            <p className="text-ink-3">Optional: <span className="font-mono text-[11.5px]">{feature.optional_env.join(' · ')}</span></p>
          ) : null}
          {setup.doc ? (
            <p className="text-ink-3">
              Step-by-step guide for IT: <span className="font-mono text-[11.5px] text-ink-2">docs/INTEGRATIONS_SETUP.md</span> › “{setup.doc}”
            </p>
          ) : null}
          {feature.updated_by ? (
            <p className="text-ink-3">Last switched by {feature.updated_by}{feature.updated_at ? ` · ${formatDateTime(feature.updated_at)}` : ''}</p>
          ) : null}
          <div className="flex flex-wrap items-center gap-2 pt-1">
            <Button type="button" variant="outline" size="sm" className="bg-surface" onClick={copyNote} data-testid={`service-copy-note-${feature.key}`}>
              <Copy aria-hidden="true" /> Copy setup note for IT
            </Button>
            {setup.path ? (
              <Link to={setup.path} className="inline-flex items-center gap-1 text-[12.5px] font-semibold text-brand-ink hover:underline">
                Open where it is used <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}
    </li>
  );
}

export function ServicesCard({ appName, recheck }) {
  const confirm = useConfirm();
  const { feature, loading, error, refresh } = useIntegrationStatus();
  // the page's "Recheck status" asks the server again (the status is cached for 5 minutes otherwise)
  const firstRecheck = useRef(recheck);
  useEffect(() => {
    if (recheck === firstRecheck.current) return;
    firstRecheck.current = recheck;
    refresh();
  }, [recheck, refresh]);
  const [openKey, setOpenKey] = useState('');
  const [switching, setSwitching] = useState('');
  const features = SERVICE_ORDER.map((key) => feature(key));
  const on = features.filter((item) => item.status === 'on').length;

  const onSwitch = async (item, next) => {
    let done = false;
    const run = async () => {
      setSwitching(item.key);
      try {
        await api.put(`/integrations/${item.key}`, { enabled: next });
        done = true;
      } finally {
        setSwitching('');
      }
    };
    if (!next) {
      const ok = await confirm({
        title: `Switch ${item.label} off?`,
        description: 'Everyone loses it until an admin switches it back on. The server setup stays as it is.',
        confirmLabel: 'Switch off',
        // a red action that deletes nothing: no trash can (confirmIcon: null)
        tone: 'danger',
        confirmIcon: null,
        onConfirm: run,
      });
      if (!ok) return;
    } else {
      try {
        await run();
      } catch (err) {
        notify.error(`Could not switch ${item.label} on`, { description: getErrorMessage(err) });
        return;
      }
    }
    if (done) {
      await refresh();
      notify.success(`${item.label} is ${next ? 'on' : 'off'}`, { description: 'Recorded in History.' });
    }
  };

  return (
    <AdminCard
      title="All connected services"
      subtitle={`Every outside service ${appName || 'the app'} can use — what works today and what IT still has to set up.`}
      icon={Plug}
      iconTone="navy"
      badge={<StatusPill tone="lime" label="New" size="sm" hideDot />}
      aside={loading && !error ? <span>Checking…</span> : <span data-testid="services-summary">{on} of {features.length} on</span>}
      bodyClassName="py-1"
      data-testid="services-card"
    >
      {error && !loading ? (
        <p className="flex flex-wrap items-center gap-2 py-3 text-[13px] text-ink-2" data-testid="services-error">
          <TriangleAlert className="h-4 w-4 text-status-amber" aria-hidden="true" />
          Couldn’t check the services. {getErrorMessage(error)}
          <Button type="button" variant="outline" size="sm" onClick={refresh}><RotateCw aria-hidden="true" /> Retry</Button>
        </p>
      ) : null}
      <ul className="divide-y divide-line-2" aria-busy={loading}>
        {features.map((item) => (
          <ServiceRow
            key={item.key}
            feature={item}
            statusInfo={serviceStatus(item, { error: Boolean(error), loading })}
            open={openKey === item.key}
            onToggle={() => setOpenKey((current) => (current === item.key ? '' : item.key))}
            onSwitch={onSwitch}
            switching={switching === item.key}
            appName={appName}
          />
        ))}
      </ul>
    </AdminCard>
  );
}

/* ------------------------------------------------------------------------------------------------ */
export function OneDriveUnlinked({ data, loading }) {
  const projects = data?.projects || [];
  const count = Number(data?.count || 0);
  if (loading && !data) return <div className="h-32 animate-pulse rounded-xl bg-surface-2" aria-label="Loading projects without a folder" />;
  if (!data) return null;
  return (
    <div data-testid="onedrive-unlinked">
      <div className="flex items-baseline justify-between gap-2">
        <UpperLabel>Active projects without a folder</UpperLabel>
        <span className="text-[12px] tabular-nums text-ink-3">{formatNumber(count)}</span>
      </div>
      {projects.length ? (
        <ul className="mt-1.5 divide-y divide-line-2">
          {projects.map((project) => (
            <li key={project.id} className="flex items-center gap-3 py-2" data-testid={`onedrive-unlinked-${project.id}`}>
              <div className="min-w-0 flex-1">
                <p className="truncate text-[13.5px] font-semibold text-ink">{project.name}</p>
                <p className="flex items-center gap-1.5 text-[12px] text-ink-3">
                  <SystemChip systemType={project.system_type} size="xs" />
                  {project.location ? <span className="truncate">{project.location}</span> : null}
                  {project.location && project.created_at ? <span aria-hidden="true">·</span> : null}
                  {project.created_at ? <span className="shrink-0">created {formatDate(project.created_at)}</span> : null}
                </p>
              </div>
              <Button asChild variant="outline" size="xs" className="shrink-0">
                <Link to={`/projects/${project.id}?tab=files`} data-testid={`onedrive-link-folder-${project.id}`}>
                  <Link2 aria-hidden="true" /> Link folder
                </Link>
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-1.5 text-[13px] text-ink-2">Every active project has a OneDrive folder.</p>
      )}
      {count > projects.length ? (
        // the Projects list cannot filter "no OneDrive folder" yet: the link says where it goes, not a number it won't show
        <Link to="/projects?status=Active" className="mt-1 inline-flex items-center gap-1 text-[13px] font-semibold text-brand-ink hover:underline" data-testid="onedrive-see-all">
          See all active projects <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
        </Link>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------------------------------------ */
const EXAMPLE_ROWS = [
  { status: 'synced', text: 'INV-2026-0142 → QuickBooks #1042', project: 'Waldorf Astoria', by: 'synced by Yahya', time: '09:12' },
  { status: 'failed', text: 'VAR-INV-0031 · Customer not found', project: 'Luxor Tower', by: 'Retry', time: '09:14' },
  { status: 'summary', text: 'Daily summary sent to Accounts', project: '6 synced, 1 failed', by: '', time: '18:00' },
];

const SYNC_PATHS = { invoice: 'invoices', variation_invoice: 'variation-invoices' };

/** 31 #7 — the daily summary for Accounts: on (with when / to how many), or why it is off. */
function DailySummaryLine({ state }) {
  if (!state) return null;
  const on = state.email_on && state.recipients > 0;
  const text = !state.email_on
    ? 'Off until Outgoing email is set up (All connected services › Outgoing email).'
    : !state.recipients
      ? 'Nobody to send it to — give a role Invoicing (people need an email address), or ask IT to set QUICKBOOKS_SUMMARY_TO.'
      : `${state.schedule_label || 'Every day'} to ${state.recipients} ${state.recipients === 1 ? 'person' : 'people'}${state.recipients_from_env ? ' (set by IT)' : ' with Invoicing'}${state.last_sent_at ? ` · last sent ${formatDateTime(state.last_sent_at, { relativeDay: true })}` : ' · nothing sent yet (only days with syncs)'}`;
  return (
    <p className="mt-3 flex items-start gap-2 border-t border-line-2 pt-3 text-[12.5px] text-ink-2" data-testid="sync-activity-daily-summary" data-state={on ? 'on' : 'off'}>
      <MailCheck className={cn('mt-px h-4 w-4 shrink-0', on ? 'text-status-green' : 'text-ink-4')} aria-hidden="true" />
      <span className="min-w-0">
        <b className="font-semibold text-ink">Daily summary to Accounts:</b>{' '}
        {on ? <StatusPill tone="green" label="On" size="sm" className="mr-1 align-[1px]" /> : null}
        {text}
      </span>
    </p>
  );
}

export function SyncActivityCard({ activity, dailySummary, loading, connected, onRetried }) {
  const items = activity?.items || [];
  const failed = Number(activity?.failed_30_days || 0);
  const [retrying, setRetrying] = useState('');
  // Retry here: the same sync the invoice page runs (POST /quickbooks/…/{id}/sync), then the feed reloads
  const retry = async (item) => {
    const path = SYNC_PATHS[item.invoice_kind];
    if (!path || !item.invoice_id) return;
    setRetrying(item.id);
    try {
      await api.post(`/quickbooks/${path}/${item.invoice_id}/sync`);
      notify.success(`${item.reference || 'Invoice'} sent to QuickBooks`);
      onRetried?.();
    } catch (err) {
      notify.error(`Could not send ${item.reference || 'the invoice'}`, { description: getErrorMessage(err) });
    } finally {
      setRetrying('');
    }
  };
  return (
    <AdminCard
      title="Sync activity"
      icon={Activity}
      iconTone="navy"
      badge={<StatusPill tone="lime" label="New" size="sm" hideDot />}
      aside={<span>{failed ? <b className="font-semibold text-status-red-ink">{failed} failed in 30 days · </b> : null}Invoices sent to QuickBooks, failures and retries</span>}
      bodyClassName="py-4"
      data-testid="sync-activity-card"
    >
      {loading && !activity ? <div className="h-24 animate-pulse rounded-xl bg-surface-2" aria-label="Loading sync activity" /> : null}
      {!loading || activity ? (
        items.length ? (
          <ul className="divide-y divide-line-2" data-testid="sync-activity-list">
            {items.map((item) => (
              <li key={item.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-[13px]" data-testid={`sync-activity-${item.status}`} data-resolved={item.resolved || undefined}>
                <StatusPill
                  tone={item.status === 'failed' ? (item.resolved ? 'gray' : 'red') : 'green'}
                  label={item.status === 'failed' ? (item.resolved ? 'Resolved' : 'Failed') : 'Synced'}
                  size="sm"
                  className="w-[74px] justify-center"
                  title={item.resolved ? 'Synced later — nothing to retry' : undefined}
                />
                <span className="min-w-0 flex-1 truncate">
                  <span className="font-mono text-[12px] text-ink-2">{item.reference || (item.invoice_kind === 'variation_invoice' ? 'Variation invoice' : 'Invoice')}</span>
                  {item.status === 'failed'
                    ? <span className={cn('font-semibold', item.resolved ? 'text-ink-3 line-through decoration-[color:var(--ink-4)]' : 'text-status-red-ink')}> · {item.error || 'Sync failed'}</span>
                    : <span className="text-ink"> → QuickBooks{item.qb_doc_number ? ` #${item.qb_doc_number}` : ''}</span>}
                  {item.project_name ? <span className="text-ink-3"> · {item.project_name}</span> : null}
                </span>
                <span className="text-[12px] text-ink-3">{item.status === 'failed' ? (item.by ? `tried by ${item.by}` : '') : item.by ? `synced by ${item.by}` : ''}</span>
                {item.status === 'failed' && !item.resolved && SYNC_PATHS[item.invoice_kind] && item.invoice_id ? (
                  <Button type="button" variant="outline" size="xs" onClick={() => retry(item)} disabled={retrying === item.id} data-testid="sync-activity-retry">
                    {retrying === item.id ? <Loader2 className="animate-spin" aria-hidden="true" /> : <RotateCw aria-hidden="true" />} Retry
                  </Button>
                ) : null}
                {item.status === 'failed' && item.project_id ? (
                  <Link
                    to={`/projects/${item.project_id}?tab=invoicing`}
                    className="inline-grid h-7 w-7 place-items-center rounded-md text-ink-3 hover:bg-surface-hover hover:text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    aria-label={`Open ${item.reference || 'the invoice'} in ${item.project_name || 'its project'}`}
                    title="Open the project's Invoicing"
                    data-testid="sync-activity-open"
                  >
                    <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                  </Link>
                ) : null}
                <span className="w-[120px] text-right text-[12px] tabular-nums text-ink-3">{formatDateTime(item.at, { relativeDay: true })}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="flex flex-col items-center gap-3 py-2 text-center" data-testid="sync-activity-empty">
            <span className="ui-icon-tile tone-navy h-11 w-11" aria-hidden="true"><Activity className="h-[22px] w-[22px]" /></span>
            <div>
              <p className="text-[14px] font-bold text-ink">No syncs yet</p>
              <p className="mx-auto mt-1 max-w-[460px] text-[13px] text-ink-2">
                When QuickBooks is connected, every invoice sync, failure and retry appears here — with a daily summary for Accounts.
              </p>
            </div>
            <div className="w-full max-w-[760px] rounded-xl border border-dashed border-line px-4 py-2 text-left" aria-label="Example of what appears here">
              <p className="flex items-center gap-2 py-1 text-[11px] font-bold uppercase tracking-[0.06em] text-ink-3">
                What you’ll see here <span className="rounded-full bg-surface-2 px-2 normal-case tracking-normal text-ink-2">Example</span>
              </p>
              <ul className="divide-y divide-line-2 opacity-80">
                {EXAMPLE_ROWS.map((row) => (
                  <li key={row.text} className="flex flex-wrap items-center gap-x-3 gap-y-0.5 py-2 text-[12.5px]">
                    {row.status === 'summary' ? (
                      <StatusPill tone="navy" label="Summary" size="sm" hideDot className="w-[74px] justify-center" />
                    ) : (
                      <StatusPill tone={row.status === 'failed' ? 'red' : 'green'} label={row.status === 'failed' ? 'Failed' : 'Synced'} size="sm" className="w-[74px] justify-center" />
                    )}
                    {/* phones: the reference wraps instead of "INV-…" — the example has to teach something */}
                    <span className={cn('min-w-0 flex-1 text-[12px] text-ink-2 sm:truncate max-sm:basis-[calc(100%-86px)]', row.status === 'summary' ? 'font-sans' : 'font-mono')}>
                      {row.text} <span className="font-sans text-ink-3">· {row.project}</span>
                    </span>
                    {row.by ? <span className="text-ink-3 max-sm:pl-[86px]">{row.by}</span> : null}
                    <span className="tabular-nums text-ink-3 max-sm:ml-auto">{row.time}</span>
                  </li>
                ))}
              </ul>
            </div>
            {!connected ? (
              <Button type="button" variant="outline" size="sm" disabled data-testid="sync-activity-connect-first">Connect QuickBooks first</Button>
            ) : null}
          </div>
        )
      ) : null}
      <DailySummaryLine state={dailySummary} />
    </AdminCard>
  );
}
